import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type {
  DataGridPremiumProps,
  GridColDef,
  GridFilterModel,
  GridListViewColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import HeldByCell from './HeldByCell';
import HeldByModal from './HeldByModal';
import { useGridColumns } from '@/hooks/useGridColumns';
import { useManagedColumnVisibility } from '@/hooks/useManagedColumnVisibility';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { useLocalization } from '@/hooks/useLocalization';
import { NoHoldingsRowsOverlay } from '@/components/table/NoHoldingsRowsOverlay';
import { GRID_KEYS } from '@/constants/gridKeys';
import { PAGE_SIZES, PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { GRID_COLUMN_WIDTH_COMPACT } from '@/constants/dimensions';
import { DEBOUNCE_MS } from '@/constants/timing';
import { formatNumberToCurrency } from '@/utils/currency';
import type { AdvisorAggregatedHolding } from '@/store/advisorAggregatedHoldingsApi';
import {
  buildAggregatedHoldingRows,
  formatAggregatedPercent,
  heldByLabel,
  type AggregatedHoldingRow,
} from '../utils/aggregatedHoldings';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

const DEFAULT_HIDDEN_FIELDS: string[] = [];

/**
 * What a cell can hold once the column's formatter has run. Every column here
 * either formats to a string or reads an `AggregatedHoldingRow` field directly,
 * and the backend-driven columns may name a field the row does not carry — hence
 * the nullish arms.
 */
type AggregatedHoldingCellValue = string | number | null | undefined;

interface AggregatedHoldingsPageGridProps {
  variant?: 'page';
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  /** Size passed through to the internal GridContainer. Defaults to 'md'. */
  size?: GridContainerSize;
}

interface AggregatedHoldingsWidgetGridProps {
  variant: 'widget';
}

export type AggregatedHoldingsGridProps = {
  records: readonly AdvisorAggregatedHolding[];
  loading: boolean;
} & (AggregatedHoldingsPageGridProps | AggregatedHoldingsWidgetGridProps);

const AggregatedHoldingsGrid = (props: AggregatedHoldingsGridProps) => {
  const { records, loading } = props;
  const { t } = useLocalization();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(GRID_KEYS.ADVISOR_AGGREGATED_HOLDINGS);

  const {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
  } = useManagedColumnVisibility<AggregatedHoldingRow>({
    defaultHiddenFields: DEFAULT_HIDDEN_FIELDS,
  });

  const [selectedHolding, setSelectedHolding] =
    useState<AggregatedHoldingRow | null>(null);
  const [lastHolding, setLastHolding] = useState<AggregatedHoldingRow | null>(
    null,
  );

  const handleOpenHolding = useCallback((row: AggregatedHoldingRow) => {
    setSelectedHolding(row);
    setLastHolding(row);
  }, []);

  const rows = useMemo(() => buildAggregatedHoldingRows(records), [records]);

  const columns = useMemo<GridColDef<AggregatedHoldingRow>[]>(() => {
    const defaultColumns: Record<string, GridColDef<AggregatedHoldingRow>> = {
      Name: {
        field: 'Name',
        headerName: t('name', 'Name'),
        flex: 2,
        minWidth: 200,
      },
      Symbol: {
        field: 'Symbol',
        headerName: t('grid.ticker', 'Ticker'),
        width: 110,
      },
      CUSIPDisplay: {
        field: 'CUSIPDisplay',
        headerName: t('grid.cusip', 'CUSIP'),
        width: GRID_COLUMN_WIDTH_COMPACT,
        valueGetter: (_value, row) => row.CUSIPDisplay ?? row.CUSIP ?? '',
      },
      Balance: {
        field: 'Balance',
        headerName: t('grid.balance', 'Balance'),
        width: 160,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatNumberToCurrency(value),
      },
      PercentHome: {
        field: 'PercentHome',
        headerName: t('grid.managedAssets', 'Managed Assets'),
        width: 150,
        align: 'right',
        headerAlign: 'right',
        aggregable: false,
        valueFormatter: (value: number) => formatAggregatedPercent(value),
      },
      HeldBy: {
        field: 'HeldBy',
        headerName: t('grid.heldBy', 'Held By'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (_value, row) =>
          heldByLabel(row.HeldBy, (count) =>
            t('heldByClientsCount', { count }),
          ),
        renderCell: (params) => (
          <HeldByCell
            holders={params.row.HeldBy}
            tabIndex={params.tabIndex}
            onOpen={() => {
              handleOpenHolding(params.row);
            }}
          />
        ),
      },
    };

    const enabled = (gridColumnsData?.Records ?? [])
      .filter((record) => record.IsEnabled)
      .sort((a, b) => a.Order - b.Order);

    const mapped = enabled.map((record): GridColDef<AggregatedHoldingRow> => {
      const base = defaultColumns[record.GridColumnDataField] as
        | GridColDef<AggregatedHoldingRow>
        | undefined;
      if (base) {
        return record.Label ? { ...base, headerName: record.Label } : base;
      }
      return {
        field: record.GridColumnDataField,
        headerName: record.Label || record.GridColumnDataField,
        flex: 1,
        minWidth: 140,
        sortable: false,
      };
    });

    return applyColumnVisibility(mapped);
  }, [t, gridColumnsData, applyColumnVisibility, handleOpenHolding]);

  const tableMinWidthPx = useMemo(
    () =>
      columns
        .filter(
          (column) =>
            (columnVisibilityModel[column.field] as boolean | undefined) !==
            false,
        )
        .reduce(
          (total, column) => total + (column.width ?? column.minWidth ?? 0),
          0,
        ),
    [columns, columnVisibilityModel],
  );

  const { ref: widgetRef, isBelow: isWidgetNarrowerThanTable } =
    useWidgetWidthIsBelow(props.variant === 'widget', tableMinWidthPx);
  const showListView = isXs || isWidgetNarrowerThanTable;

  const listViewColDef: GridListViewColDef<AggregatedHoldingRow> = useMemo(
    () => ({
      field: 'listColumn',
      headerName: '',
      flex: 1,
      minWidth: 0,
      renderCell: (params) => {
        const visibilityModel = params.api.state.columns.columnVisibilityModel;

        const visibleFields = params.api
          .getAllColumns()
          .map((column) => column.field)
          .filter(
            (field) =>
              field !== 'listColumn' &&
              !field.startsWith('__') &&
              (visibilityModel[field] as boolean | undefined) !== false,
          );

        const label = (field: string) =>
          params.api.getColumn(field).headerName || field;

        const value = (field: string) => {
          const cell = params.api.getCellParams<
            AggregatedHoldingRow,
            AggregatedHoldingCellValue,
            AggregatedHoldingCellValue
          >(params.id, field).formattedValue;
          if (cell == null || cell === '') return '-';
          return String(cell);
        };

        return (
          <Box
            sx={{
              width: '100%',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {visibleFields.map((field) => {
              if (field === 'Name') {
                return (
                  <Typography
                    key={field}
                    variant='body2'
                    color='text.primary'
                    sx={{
                      fontWeight: '600 !important',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    {value(field)}
                  </Typography>
                );
              }
              if (field === 'HeldBy') {
                return (
                  <Box
                    key={field}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Typography
                      component='span'
                      variant='body2'
                      color='text.secondary'
                      sx={{ fontWeight: 700 }}
                    >
                      {label(field)}:
                    </Typography>
                    <HeldByCell
                      holders={params.row.HeldBy}
                      tabIndex={params.tabIndex}
                      onOpen={() => {
                        handleOpenHolding(params.row);
                      }}
                    />
                  </Box>
                );
              }
              return (
                <Typography
                  key={field}
                  variant='body2'
                  color='text.secondary'
                  sx={{ textAlign: 'left', wordBreak: 'break-word' }}
                >
                  <Box
                    component='span'
                    sx={{ fontWeight: 700 }}
                  >
                    {label(field)}:
                  </Box>{' '}
                  {value(field)}
                </Typography>
              );
            })}
          </Box>
        );
      },
    }),
    [handleOpenHolding],
  );

  const modeProps: Partial<DataGridPremiumProps<AggregatedHoldingRow>> =
    props.variant === 'widget'
      ? {
          autoHeight: true,
          hideFooter: true,
          disableColumnSorting: true,
          disableColumnMenu: true,
        }
      : {
          rowCount: props.rowCount,
          pagination: true,
          paginationMode: 'server',
          paginationModel: props.paginationModel,
          onPaginationModelChange: props.onPaginationModelChange,
          pageSizeOptions: [...PAGE_SIZE_OPTIONS.STANDARD],
          sortingMode: 'server',
          sortModel: props.sortModel,
          onSortModelChange: props.onSortModelChange,
          ...(props.filterModel
            ? {
                filterModel: props.filterModel,
                onFilterModelChange: props.onFilterModelChange,
              }
            : {}),
          showToolbar: true,
        };

  const grid = (
    <>
      <DataGridPremium<AggregatedHoldingRow>
        rows={rows}
        columns={columns}
        loading={loading || gridColumnsLoading}
        getRowId={(row) => row.id}
        {...modeProps}
        // The columns actually instantiated below depend on each advisor's
        // own `/api/user/gridColumns` config (`mapped`), plus CUSIP which
        // is always appended regardless of config (see the `TODO(IU-117)`
        // above). If an advisor has, say, `Name` or `Symbol` disabled, that
        // field never becomes a column at all. The backend already matches
        // `searchTerm` against the full record server-side and returns the
        // right rows in `records`/`rows` — but MUI's *client-side* quick
        // filter (the default `filterMode='client'`) re-filters those rows
        // using only the columns that exist, so a row whose only match was
        // on a non-instantiated field gets silently dropped again on the
        // client. `filterMode='server'` tells MUI to trust `rows` as
        // already filtered and skip that re-filtering entirely, which also
        // makes the column-visibility-only version of this bug (CUSIP
        // hidden via the column menu) moot — there is no more client
        // re-filtering for a hidden column to be excluded from.
        //
        // Trade-off: the default toolbar's "Filters" button (MUI's
        // per-column filter panel, distinct from the quick filter search
        // box above) filters `rows` locally too. With `filterMode='server'`
        // it would still open and let an advisor build a per-column filter,
        // but that filter would never be applied — not server-side (nothing
        // sends it to the backend) and not client-side (MUI no longer
        // filters at all) — silently doing nothing. That control isn't part
        // of IU-307/IU-117's requirements (which only call for the quick
        // filter and the existing Client/Group filter) and isn't covered by
        // any test in this file, so `disableColumnFilter` removes it from
        // the toolbar rather than leaving a control that looks functional
        // but silently does nothing.
        filterMode='server'
        disableColumnFilter
        disablePivoting
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        disableRowSelectionOnClick
        listView={showListView}
        {...(showListView ? { listViewColumn: listViewColDef } : {})}
        getRowHeight={() => (showListView ? 'auto' : null)}
        slots={{
          ...(isNative
            ? {
                toolbar: (
                  props: React.ComponentProps<typeof CustomToolbarForNative>,
                ) => <CustomToolbarForNative {...props} />,
              }
            : {}),
          noRowsOverlay: NoHoldingsRowsOverlay,
          noResultsOverlay: NoHoldingsRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: DEBOUNCE_MS.NONE },
            // The native toolbar's own "filters" button suffers the same
            // silent-no-op problem as MUI's default one (see the
            // `filterMode='server'` comment above) — `disableColumnFilter`
            // has no effect on this custom component, so it's hidden
            // explicitly here instead.
            showFilters: false,
          },
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: PAGE_SIZES.STANDARD } },
          filter: {
            filterModel: {
              items: [],
            },
          },
        }}
      />
      <HeldByModal
        open={selectedHolding != null}
        onClose={() => {
          setSelectedHolding(null);
        }}
        holders={(selectedHolding ?? lastHolding)?.HeldBy ?? []}
        holdingName={(selectedHolding ?? lastHolding)?.Name ?? ''}
      />
    </>
  );

  if (props.variant === 'widget') {
    return (
      <Box
        ref={widgetRef}
        sx={{ width: '100%' }}
      >
        {grid}
      </Box>
    );
  }

  return (
    <GridContainer
      isWidget={false}
      size={props.size}
    >
      {grid}
    </GridContainer>
  );
};

export default AggregatedHoldingsGrid;
